import { useState, type ReactNode } from 'react'
import { usePelviaCareStatus } from '@pelvia/shared/hooks/patient/useBilling'
import {
  useAkuteStatus,
  useAkuteEnroll,
  missingDemographicsFrom,
  type AkuteStatusValue,
} from '@pelvia/shared/hooks/patient/useAkute'
import { useScreenerGate } from '@/hooks/useScreenerGate'
import { useDownloadConsultNote, type ConsultNoteFormat } from '@/hooks/useDownloadConsultNote'
import PelviaCareCheckout from '@/pages/patient/PelviaCareCheckout'
import DemographicsModal from '@/components/DemographicsModal'
import SendNoteModal from '@/components/SendNoteModal'

/**
 * Single source of truth for the patient-facing PelviaCare+ flow, shared by the
 * homepage cards so they behave identically. It exposes:
 *
 *  - `isMember` — paid a PelviaCare+ order. Driven by billing (set the instant
 *    payment confirms), NOT the Akute note status, so a freshly-paid patient is
 *    a member immediately even while the note is still being generated.
 *  - `noteStatus` / `noteSigned` — the consult note's progress in Akute.
 *  - `startCheckout()` — non-members: screener gate, then the Stripe checkout.
 *  - `sendNote()` — members: (re)send the note to the clinician (Akute enroll),
 *    falling back to the demographics modal if anything's still missing.
 *  - `modals` — the checkout / demographics / screener modals to render once.
 */
export interface PelviaCare {
  /** Whether PelviaCare+ is live (server master switch). `undefined` until the
   *  status query resolves, so surfaces can hold rendering to avoid flashing the
   *  feature or the "coming soon" card before we know which to show. */
  available: boolean | undefined
  isMember: boolean
  noteStatus?: AkuteStatusValue
  noteSigned: boolean
  /** ISO timestamp the note was signed off, when signed. */
  noteSignedAt: string | null
  /** Display name of the clinician who signed. Null for members enrolled before
   *  the name was captured, so every surface must read without it. */
  clinicianName: string | null
  /** Whether a send would actually do something — false once the note is with a
   *  clinician (`note_pushed`, no-op until in-place update ships) or signed. */
  canSend: boolean
  sendingNote: boolean
  downloading: boolean
  message: string | null
  clearMessage: () => void
  startCheckout: () => void
  sendNote: () => void
  /** Open the note read-only (for the awaiting-sign-off state). */
  previewNote: () => void
  /** Download the signed consult note (Part C); gated to signed_off server-side. */
  downloadNote: (format?: ConsultNoteFormat) => void
  modals: ReactNode
}

export function usePelviaCare(): PelviaCare {
  const membership = usePelviaCareStatus()
  const akuteStatus = useAkuteStatus()
  const enroll = useAkuteEnroll()
  const { gate, modal: screenerModal } = useScreenerGate()
  const { download, downloading } = useDownloadConsultNote()

  const [checkoutOpen, setCheckoutOpen] = useState(false)
  const [demographicsOpen, setDemographicsOpen] = useState(false)
  // The send/preview modal; `mode` distinguishes the active send (picker) from a
  // read-only look at a note already with the clinician.
  const [noteModal, setNoteModal] = useState<'send' | 'preview' | null>(null)
  const [pendingClinician, setPendingClinician] = useState<string | null>(null)
  const [missingFields, setMissingFields] = useState<string[]>([])
  const [message, setMessage] = useState<string | null>(null)

  // Master switch (Config.PELVIACARE_ENABLED), surfaced via the akute status.
  // Undefined while the query is in flight so callers can defer their decision.
  const available = akuteStatus.data?.enabled
  const isMember = !!membership.data?.is_member
  const noteStatus = akuteStatus.data?.status
  const noteSigned = noteStatus === 'signed_off'
  const noteSignedAt = akuteStatus.data?.signed_off_at ?? null
  const clinicianName = akuteStatus.data?.clinician_name ?? null
  // A note already pushed (awaiting sign-off) or signed shouldn't be re-sent —
  // enroll no-ops on the existing note. In-place update is a separate follow-up.
  const canSend = noteStatus !== 'note_pushed' && noteStatus !== 'signed_off'

  async function startCheckout() {
    // Offer the PHQ-9 / GAD-7 screener first if no score yet, then checkout.
    if (!(await gate())) return
    setCheckoutOpen(true)
  }

  // Start the send: open the picker (+ preview). The actual send runs on confirm.
  function sendNote() {
    setMessage(null)
    setNoteModal('send')
  }

  // Read-only look at the note a clinician is currently reviewing.
  function previewNote() {
    setMessage(null)
    setNoteModal('preview')
  }

  function downloadNote(format: ConsultNoteFormat = 'pdf') {
    download(format)
  }

  function confirmSend(clinicianId: string) {
    setPendingClinician(clinicianId)
    enroll.mutate(clinicianId, {
      onSuccess: (res) => {
        setNoteModal(null)
        setDemographicsOpen(false)
        setMessage(
          res.status === 'signed_off'
            ? 'Your note is signed and with your care team.'
            : "Sent to your clinician — they'll review and sign your note within 72 hours.",
        )
      },
      onError: (err) => {
        const missing = missingDemographicsFrom(err)
        if (missing) {
          setMissingFields(missing)
          setNoteModal(null)
          setDemographicsOpen(true)
        } else {
          setMessage("Couldn't send your note right now. Please try again.")
        }
      },
    })
  }

  const modals = (
    <>
      {checkoutOpen && <PelviaCareCheckout onClose={() => setCheckoutOpen(false)} />}
      {noteModal && (
        <SendNoteModal
          mode={noteModal}
          selectedId={akuteStatus.data?.clinician_user_id}
          sending={enroll.isPending}
          onClose={() => setNoteModal(null)}
          onConfirm={confirmSend}
        />
      )}
      {demographicsOpen && (
        <DemographicsModal
          missingFields={missingFields}
          onClose={() => setDemographicsOpen(false)}
          onSaved={() => pendingClinician && confirmSend(pendingClinician)}
        />
      )}
      {screenerModal}
    </>
  )

  return {
    available,
    isMember,
    noteStatus,
    noteSigned,
    noteSignedAt,
    clinicianName,
    canSend,
    sendingNote: enroll.isPending,
    downloading,
    message,
    clearMessage: () => setMessage(null),
    startCheckout,
    sendNote,
    previewNote,
    downloadNote,
    modals,
  }
}

/** Human copy for the consult note's Akute progress, for member surfaces. */
export function noteStatusLine(
  noteStatus: AkuteStatusValue | undefined,
  signedAt?: string | null,
  clinicianName?: string | null,
): { text: string; tone: 'ok' | 'warn' | 'err' } {
  switch (noteStatus) {
    case 'signed_off': {
      // Both clauses are optional: members who enrolled before migration 049 have
      // no stored name, and signed_off_at can lag the status from the webhook.
      const by = clinicianName ? ` by ${clinicianName}` : ''
      const on = signedAt ? ` on ${formatSignedDate(signedAt)}` : ''
      return { text: `Your consult note was signed${by}${on}.`, tone: 'ok' }
    }
    case 'note_pushed':
      return { text: 'Your consult note is with your clinician for sign-off.', tone: 'warn' }
    case 'needs_resubmit':
      return {
        text: 'Your clinician asked you to resubmit your note with your latest information.',
        tone: 'warn',
      }
    case 'error':
      return { text: "The last send didn't complete — try again.", tone: 'err' }
    default:
      // paid, fulfillment still running (or just synced) — note is on its way.
      return { text: "We're preparing your consult note for your clinician.", tone: 'warn' }
  }
}

/** Friendly date for the sign-off line; falls back to the raw value if unparseable. */
function formatSignedDate(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}
